import { create } from 'zustand';

/**
 * Chat search + jump-to-message state for the OPEN conversation (search is
 * per conversation, PLAN D1). Everything is tagged with `channelId`, so a
 * result, a jump or a highlight can never apply to another conversation.
 *
 * Results live here rather than in chatStore on purpose: they are a window
 * onto the server, not part of the contiguous message buffer (D12).
 */

const EMPTY_RESULTS = {
  results: [],
  nextCursor: null,
  complete: true,
  total: null,
  totalCapped: false,
  historyDays: null,
  status: 'idle', // 'idle' | 'loading' | 'error'
  error: null, // null | { key, value? }
  terms: [],
  followUps: 0,
  ran: false,
};

const INITIAL = {
  channelId: null,
  open: false,
  input: '',
  sort: 'newest',
  ...EMPTY_RESULTS,
  jumpRequest: null, // { channelId, seq, nonce }
  jumpStatus: 'idle', // 'idle' | 'loading' | 'far' | 'missing'
  jumpSeq: null, // the seq jumpStatus refers to
  highlight: null, // { channelId, seq }
  jumpedAway: false,
};

let nonce = 0;

export const useChatSearchStore = create((set, get) => ({
  ...INITIAL,

  /** Show the results panel for `channelId`, keeping the input. */
  openFor: (channelId) =>
    set((s) => (s.channelId === channelId ? { open: true } : { ...INITIAL, channelId, open: true, input: s.input })),

  close: () => set({ open: false, ...EMPTY_RESULTS }),

  /** Full reset — a conversation switch. */
  reset: (channelId = null) => set({ ...INITIAL, channelId }),

  setInput: (input) => set({ input }),
  setSort: (sort) => set({ sort }),

  startSearch: (terms) => set({ ...EMPTY_RESULTS, status: 'loading', terms, ran: true }),
  setError: (error) => set({ status: 'error', error }),

  /** Merge one response page (`append` for a follow-up / Keep searching). */
  applyPage: (page, { append }) =>
    set((s) => ({
      results: append ? [...s.results, ...(page.messages || [])] : page.messages || [],
      nextCursor: page.next_cursor ?? null,
      complete: page.complete !== false,
      total: append ? s.total : page.total ?? null,
      totalCapped: append ? s.totalCapped : Boolean(page.total_capped),
      historyDays: page.history_days ?? null,
      status: 'idle',
      error: null,
    })),

  setFollowUps: (followUps) => set({ followUps }),

  requestJump: (channelId, seq) => {
    nonce += 1;
    set({ jumpRequest: { channelId, seq, nonce }, jumpSeq: seq });
  },
  /**
   * A jump request runs ONCE. The message view unmounts when you leave the
   * Chat panel and mounts again when you come back; the request used to stay
   * in the store and replay — back to the searched message, highlighted
   * again, after you had already jumped to the present (owner, 29.09.2026).
   */
  handledJumpNonce: null,
  markJumpHandled: (n) => set({ handledJumpNonce: n }),
  /** Jump to present / back at the bottom: the old jump is over. */
  dropJump: () => set((s) => ({
    jumpRequest: null,
    handledJumpNonce: s.jumpRequest ? s.jumpRequest.nonce : s.handledJumpNonce,
    highlight: null,
    jumpedAway: false,
  })),
  setJumpStatus: (jumpStatus, jumpSeq = get().jumpSeq) => set({ jumpStatus, jumpSeq }),
  setHighlight: (highlight) => set({ highlight }),
  clearHighlight: () => set({ highlight: null }),
  setJumpedAway: (jumpedAway) => set((s) => (s.jumpedAway === jumpedAway ? {} : { jumpedAway })),
}));
